import React, { useState, useEffect, useCallback, useRef } from "react";
import "../styles/responsive/pages/Transactions.css";
import "../styles/enterprise.css";
import { useSelector } from "react-redux";
import { useTheme } from "../context/ThemeContext";
import { useIsMobile } from "../hooks/useMediaQuery";
import {
  Filter,
  Columns,
  Plus,
  ExternalLink,
  X,
  Check,
  Copy,
  CheckCircle,
  Clock,
  AlertCircle,
  ArrowUpRight,
  ArrowDownLeft,
  Wallet,
  FileText,
  Loader2,
  Shield,
  AlertTriangle,
  Download,
  Inbox,
  MoreVertical,
  Repeat,
} from "lucide-react";
import AdvancedFilterModal from "../components/common/AdvancedFilterModal";
import StatusBadge from "../components/common/StatusBadge";
import EmptyState from "../components/common/EmptyState";
import { TableSkeleton } from "../components/common/Skeleton";
import RequireFeature from "../components/common/RequireFeature";
import UpsellModal from "../components/common/UpsellModal";
import PageHeaderPro from "../components/common/PageHeaderPro";
import TransactionSummary from "../components/transactions/TransactionSummary";
import FilterTabs from "../components/common/FilterTabs";
import AssetBadge from "../components/common/AssetBadge";
import {
  TokenChainLogo,
  TokenLogo,
  ChainLogo,
} from "../components/common/CryptoLogo";
import { chainDisplayLabel } from "../utils/assetLogos";
import Pager from "../components/common/Pager";
import EntSearch from "../components/common/EntSearch";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { fetchWithAuth } from "../utils/api";
import {
  formatCrypto,
  formatUSD,
  formatCount,
  truncateHash,
} from "../utils/formatAmount";
import { explorerTxUrl } from "../utils/explorerUrls";
import useDebounce from "../hooks/useDebounce";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:5000/api";

// Columns that carry the identity of a transaction — always rendered, never
// hideable from the Columns dropdown.
const LOCKED_COLUMNS = ["transaction", "type", "payment"];

const Transactions = () => {
  const { token, user } = useSelector((state) => state.auth);
  const { mode } = useTheme();
  const isMobile = useIsMobile();
  const isSuperAdmin = user?.role_id === 1 || user?.roleId === 1;

  const [activeTab, setActiveTab] = useState("payments");
  const [transactions, setTransactions] = useState([]);
  // Sequence guards for the two list fetches.
  //
  // ThemeContext starts `mode` at 'test' and then loads the account's saved mode
  // from localStorage once userId resolves, so a refresh fires this fetch twice:
  // once for testnet, once for live. Neither call was cancellable, so whichever
  // RESOLVED last won — not whichever was issued last — and the table showed
  // the testnet count or the live count depending on network jitter. That is the
  // "sometimes 12, sometimes 30" flicker.
  const txReqSeq = useRef(0);
  const statsReqSeq = useRef(0);
  const [stats, setStats] = useState({
    all: 0,
    succeeded: 0,
    refunded: 0,
    disputed: 0,
    failed: 0,
    uncaptured: 0,
    not_supported_token: 0,
    partially_paid: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  // A refetch is not the same as a first load. On the first one there is nothing
  // to show, so a skeleton is right. On a filter change there IS something —
  // the previous result — and replacing it with a skeleton throws the page's
  // layout away and back, which reads as a flicker rather than a transition.
  // The old rows stay, dimmed, until the new ones are ready.
  const [isRefetching, setIsRefetching] = useState(false);
  const hasLoadedOnceRef = useRef(false);
  const [fetchError, setFetchError] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  // Auto-conversion audit trail for the open transaction (PRD §3.2).
  const [conversion, setConversion] = useState(null);

  const [searchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState(() => {
    const known = ["all", "succeeded", "not_supported_token"];
    const initial = searchParams.get("status");
    return known.includes(initial) ? initial : "all";
  });
  const [searchQuery, setSearchQuery] = useState(
    () => searchParams.get("search") || "",
  );
  const debouncedSearchQuery = useDebounce(searchQuery, 400);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("DESC");
  const navigate = useNavigate();

  // BUG-07: keys must match the columns actually rendered in the pro-table so
  // toggling a column truly shows/hides it.
  const [visibleColumns, setVisibleColumns] = useState({
    status: true,
    transaction: true,
    type: true,
    payment: true,
    asset: true,
    amount: true,
    date: true,
  });
  const [showColumnDropdown, setShowColumnDropdown] = useState(false);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState({});
  const [typeFilter, setTypeFilter] = useState("all");
  const [copiedTxId, setCopiedTxId] = useState(false);
  const [copiedTxHash, setCopiedTxHash] = useState(false);
  // R-1: upsell shown when a Free user taps the locked Export button.
  const [showExportUpsell, setShowExportUpsell] = useState(false);

  const [merchants, setMerchants] = useState([]);
  const [merchantFilter, setMerchantFilter] = useState("all");
  const [isMerchantsLoading, setIsMerchantsLoading] = useState(false);

  const filterDropdownRef = useRef(null);
  const columnDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        filterDropdownRef.current &&
        !filterDropdownRef.current.contains(event.target)
      ) {
        setShowFilterDropdown(false);
      }
      if (
        columnDropdownRef.current &&
        !columnDropdownRef.current.contains(event.target)
      ) {
        setShowColumnDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const fetchTransactions = useCallback(async () => {
    if (!token) return;

    // hasLoadedOnce is read through a ref so this callback does not need it as
    // a dependency and re-create itself on every load.
    if (hasLoadedOnceRef.current) setIsRefetching(true);
    else setIsLoading(true);
    setFetchError(false);
    const seq = ++txReqSeq.current;
    try {
      const params = new URLSearchParams({
        page: currentPage,
        limit: pageSize,
        sortBy,
        sortOrder,
      });

      params.append("isTestnet", mode === "test" ? "true" : "false");

      if (statusFilter !== "all") {
        params.append("status", statusFilter);
      }
      if (typeFilter !== "all") {
        params.append("type", typeFilter);
      }
      if (debouncedSearchQuery) {
        params.append("search", debouncedSearchQuery);
      }
      if (advancedFilters.startDate) {
        params.append("startDate", advancedFilters.startDate);
      }
      if (advancedFilters.endDate) {
        params.append("endDate", advancedFilters.endDate);
      }
      if (advancedFilters.amountMin) {
        params.append("amountMin", advancedFilters.amountMin);
      }
      if (advancedFilters.amountMax) {
        params.append("amountMax", advancedFilters.amountMax);
      }
      if (advancedFilters.network && advancedFilters.network !== "all") {
        params.append("network", advancedFilters.network);
      }
      if (advancedFilters.currency && advancedFilters.currency !== "all") {
        params.append("currency", advancedFilters.currency);
      }
      if (isSuperAdmin && merchantFilter && merchantFilter !== "all") {
        params.append("merchantId", merchantFilter);
      }
      // BUG-07: dust is filtered server-side so pagination/totals stay consistent.
      params.append("hideDust", "true");
      // Hidden only where BOTH are true: money nobody asked for, in a token the
      // merchant does not accept. Untracked-but-supported payments are real
      // money and still listed; an invoice or order paid in the wrong token is
      // still listed too, because something was asked for and the merchant
      // needs to see what came back. Filtered server-side for the same reason
      // as dust — doing it here would leave pagination and the tab counts
      // describing more rows than the table shows.
      params.append("hideUntrackedUnsupported", "true");

      const response = await fetchWithAuth(
        `${API_BASE_URL}/transactions?${params}`,
      );
      const data = await response.json();

      // A newer request has already been issued; this answer describes a query
      // nobody is asking any more.
      if (seq !== txReqSeq.current) return;

      if (data.success) {
        setTransactions(data.data.transactions);
        setTotalPages(data.data.pagination.totalPages);
        setTotalItems(data.data.pagination.total);
      }
    } catch (error) {
      if (seq !== txReqSeq.current) return;
      console.error("Failed to fetch transactions:", error);
      setFetchError(true);
    } finally {
      // Only the newest request may clear the spinner, or a superseded one
      // flips it off while the real fetch is still running.
      if (seq === txReqSeq.current) {
        setIsLoading(false);
        setIsRefetching(false);
        hasLoadedOnceRef.current = true;
      }
    }
  }, [
    token,
    currentPage,
    pageSize,
    statusFilter,
    typeFilter,
    debouncedSearchQuery,
    sortBy,
    sortOrder,
    advancedFilters,
    mode,
    isSuperAdmin,
    merchantFilter,
  ]);

  // Tab counts have to describe the same result set the table is showing, so
  // this sends every filter the list sends EXCEPT status — status is the
  // dimension being counted. hideDust matches the list unconditionally.
  const fetchStats = useCallback(async () => {
    if (!token) return;

    const seq = ++statsReqSeq.current;
    try {
      const params = new URLSearchParams();
      params.append("isTestnet", mode === "test" ? "true" : "false");

      if (typeFilter !== "all") {
        params.append("type", typeFilter);
      }
      if (debouncedSearchQuery) {
        params.append("search", debouncedSearchQuery);
      }
      if (advancedFilters.startDate) {
        params.append("startDate", advancedFilters.startDate);
      }
      if (advancedFilters.endDate) {
        params.append("endDate", advancedFilters.endDate);
      }
      if (advancedFilters.amountMin) {
        params.append("amountMin", advancedFilters.amountMin);
      }
      if (advancedFilters.amountMax) {
        params.append("amountMax", advancedFilters.amountMax);
      }
      if (advancedFilters.network && advancedFilters.network !== "all") {
        params.append("network", advancedFilters.network);
      }
      if (advancedFilters.currency && advancedFilters.currency !== "all") {
        params.append("currency", advancedFilters.currency);
      }
      if (isSuperAdmin && merchantFilter && merchantFilter !== "all") {
        params.append("merchantId", merchantFilter);
      }
      params.append("hideDust", "true");
      params.append("hideUntrackedUnsupported", "true");

      const response = await fetchWithAuth(
        `${API_BASE_URL}/transactions/stats?${params}`,
      );
      const data = await response.json();

      // Same guard as the list. The tab counts have to describe the result set
      // on screen, so a stale stats answer landing after a fresh one would put
      // the testnet counts above the live table.
      if (seq !== statsReqSeq.current) return;

      if (data.success) {
        // Merge over the defaults so a status the backend omits reads 0 rather
        // than undefined.
        setStats((prev) => ({ ...prev, ...data.data }));
      }
    } catch (error) {
      if (seq !== statsReqSeq.current) return;
      console.error("Failed to fetch transaction stats:", error);
    }
  }, [
    token,
    mode,
    typeFilter,
    debouncedSearchQuery,
    advancedFilters,
    isSuperAdmin,
    merchantFilter,
  ]);

  const fetchMerchants = useCallback(async () => {
    if (!token || !isSuperAdmin) return;

    setIsMerchantsLoading(true);
    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/merchants?limit=100`,
      );
      const data = await response.json();

      if (data.success && data.data) {
        setMerchants(data.data.merchants || []);
      }
    } catch (error) {
      console.error("Failed to fetch merchants:", error);
    } finally {
      setIsMerchantsLoading(false);
    }
  }, [token, isSuperAdmin]);

  useEffect(() => {
    fetchTransactions();
    fetchStats();
  }, [fetchTransactions, fetchStats]);

  useEffect(() => {
    if (isSuperAdmin) {
      fetchMerchants();
    }
  }, [isSuperAdmin, fetchMerchants]);

  // PRD §3.2: when a transaction detail is open, load its auto-conversion (if any).
  useEffect(() => {
    const paymentId = selectedTransaction?.transactionId;
    if (!token || !paymentId) {
      setConversion(null);
      return;
    }
    // A conversion writes two ledger legs, with ids CONV-<conversionId>-DR/CR.
    // Their payment_id is the original payment's, not their own, so looking one
    // up by payment_id finds nothing and the leg reports "not converted" — the
    // exact opposite of what it is. The conversion id is right there in the leg
    // id, so legs are looked up by that instead.
    const legMatch = /^CONV-(.+)-(?:DR|CR)$/.exec(String(paymentId));
    const query = legMatch
      ? `conversion_id=${encodeURIComponent(legMatch[1])}`
      : `payment_id=${encodeURIComponent(paymentId)}`;
    let cancelled = false;
    (async () => {
      try {
        const response = await fetchWithAuth(
          `${API_BASE_URL}/merchant/conversions?${query}`,
        );
        const data = await response.json();
        if (cancelled) return;
        setConversion(
          data?.success ? data.data?.conversions?.[0] || null : null,
        );
      } catch (error) {
        if (!cancelled) setConversion(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, selectedTransaction]);

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    setCurrentPage(1);
  };

  const handleApplyAdvancedFilters = (filters, sortConfig) => {
    setAdvancedFilters(filters);
    if (sortConfig.sortBy) {
      setSortBy(sortConfig.sortBy);
      setSortOrder(sortConfig.sortOrder);
    }
    setCurrentPage(1);
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (advancedFilters.startDate || advancedFilters.endDate) count++;
    if (advancedFilters.amountMin || advancedFilters.amountMax) count++;
    if (advancedFilters.network && advancedFilters.network !== "all") count++;
    if (advancedFilters.currency && advancedFilters.currency !== "all") count++;
    if (typeFilter !== "all") count++;
    if (isSuperAdmin && merchantFilter !== "all") count++;
    return count;
  };

  const clearAllFilters = () => {
    setStatusFilter("all");
    setTypeFilter("all");
    setSearchQuery("");
    setAdvancedFilters({});
    setSortBy("created_at");
    setSortOrder("DESC");
    setMerchantFilter("all");
    setCurrentPage(1);
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "ASC" ? "DESC" : "ASC");
    } else {
      setSortBy(column);
      setSortOrder("DESC");
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // PRD §3.2: pending-state copy for non-completed conversions.
  const CONVERSION_PENDING_LABELS = {
    queued: "Conversion pending",
    quoting: "Conversion pending",
    quoted: "Quoted — awaiting settlement",
    executing: "Conversion pending",
    failed_no_route: "Conversion failed — no route",
    failed_slippage: "Conversion failed — slippage",
    failed_gas: "Conversion failed — gas",
    stuck_bridge: "Conversion stuck — bridge",
  };

  // Fiat codes are the only things that may carry a currency symbol. Everything
  // else is a token amount and is printed with its own ticker.
  //
  // This used to allow-list USDT/USDC/TRX as "crypto" and fall through to
  // `$${formatUSD(amount)}` for anything else — so every other token had its
  // on-chain amount rendered as dollars. A payment of 0.001072 ETH showed as
  // "$0.00" against a USD value of $3.00, and 2.5 MATIC showed as "$2.50",
  // which is worse: it is wrong and looks entirely plausible.
  const FIAT_CODES = new Set(["USD", "EUR", "GBP", "INR", "AED", "CAD", "AUD"]);

  const formatAmountLocal = (amount, currency, amountUsd = null) => {
    if (amountUsd !== null && amountUsd !== undefined) {
      return `$${formatUSD(amountUsd)}`;
    }
    const code = String(currency || "").toUpperCase();
    if (!code || FIAT_CODES.has(code)) {
      return `$${formatUSD(amount)}`;
    }
    return `${formatCrypto(amount)} ${code}`;
  };

  // TXN-03: a row counts as "zero/dust" when its on-chain amount is 0
  // (or below token precision). We treat amount <= 0 as dust here; amountUsd
  // is checked too so fiat-priced rows are handled consistently.
  const isDustTransaction = (tx) => {
    const amount = Number(tx.amount);
    const usd = tx.amountUsd != null ? Number(tx.amountUsd) : null;
    const amountIsZero = !Number.isFinite(amount) || amount <= 0;
    const usdIsZero = usd != null ? usd <= 0 : true;
    return amountIsZero && usdIsZero;
  };

  // TXN-03: a genuine zero-value payment marked succeeded is flagged as an anomaly.
  const isZeroValueAnomaly = (tx) =>
    normalizeTransactionStatus(tx.status) === "succeeded" &&
    isDustTransaction(tx);

  const normalizeTransactionStatus = (status) => {
    if (status === "partially_paid") return "succeeded";
    return status;
  };

  const getStatusDisplayText = (status) => {
    const normalizedStatus = normalizeTransactionStatus(status);
    if (normalizedStatus === "not_supported_token") return "Not Supported";
    return normalizedStatus;
  };

  const getConfirmationBadgeClass = (confirmationStatus) => {
    const classes = {
      pending: "confirmation-pending",
      confirmed: "confirmation-confirmed",
      failed: "confirmation-failed",
    };
    return classes[confirmationStatus] || "confirmation-pending";
  };

  const getConfirmationDisplayText = (tx) => {
    if (tx.confirmationStatus === "confirmed") {
      return "Confirmed";
    }
    if (tx.confirmationStatus === "failed" || tx.reorged) {
      return "Failed";
    }
    if (tx.blocksConfirmed !== undefined && tx.requiredConfirmations) {
      return `${tx.blocksConfirmed}/${tx.requiredConfirmations} confirmations`;
    }
    return "Pending confirmation";
  };

  // FIX 3: Payment status badge label. Mirrors Invoices.formatStatusDisplay so
  // Partial/Paid/Overpaid read identically across pages (no shared import).
  const formatPaymentStatusDisplay = (status) => {
    const statusMap = {
      paid: "Paid",
      partially_paid: "Partially Paid",
      paid_unsupported_token: "Unsupported Token",
      overpaid: "Overpaid",
      overdue: "Overdue",
      pending: "Pending",
      open: "Open",
      draft: "Draft",
      cancelled: "Cancelled",
      refunded: "Refunded",
    };
    return (
      statusMap[status.toLowerCase()] ||
      status.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
    );
  };

  /**
   * What a transaction was raised against, not what the row is stored as.
   *
   * Every incoming payment is stored with type 'payment', so the column said
   * "Payment" on every row and distinguished nothing. What a merchant actually
   * needs to tell apart is what the money was for:
   *
   *   Invoice    settles an invoice they issued
   *   Order      settles a checkout session, against their own order id
   *   Untracked  arrived at a wallet address with no invoice or order behind
   *              it — nobody asked for it, and it will not reconcile against
   *              anything in their system
   *
   * Payouts and refunds keep their own identity: they are money leaving, not a
   * payment to be attributed.
   */
  const txKind = (tx) => {
    const type = String(tx?.type || "").toLowerCase();

    if (type === "payout") return { label: "Payout", tone: "out" };
    if (type === "refund") return { label: "Refund", tone: "out" };
    if (type === "conversion" || type === "conversion_debit") {
      return { label: "Conversion", tone: "neutral" };
    }

    if (tx?.invoiceId) return { label: "Invoice", tone: "in" };
    if (tx?.sessionId) return { label: "Order", tone: "in" };
    // A payment link still produces a session, so this only catches the case
    // where the link is recorded but the session is not.
    if (tx?.paymentLinkId) return { label: "Order", tone: "in" };

    if (type === "payment") return { label: "Untracked", tone: "untracked" };
    return {
      label: type ? type.charAt(0).toUpperCase() + type.slice(1) : "—",
      tone: "neutral",
    };
  };

  const handleCopyTxId = (txId) => {
    navigator.clipboard.writeText(txId);
    setCopiedTxId(true);
    setTimeout(() => setCopiedTxId(false), 2000);
  };

  const handleCopyTxHash = (hash) => {
    navigator.clipboard.writeText(hash);
    setCopiedTxHash(true);
    setTimeout(() => setCopiedTxHash(false), 2000);
  };

  const generateTransactionEvents = (tx) => {
    const events = [];

    if (tx.status === "succeeded") {
      events.push({
        type: "payment_succeeded",
        message: "Payment succeeded",
        date: tx.updatedAt || tx.createdAt,
      });
    }
    if (tx.status === "failed") {
      events.push({
        type: "payment_failed",
        message: "Payment failed",
        date: tx.updatedAt || tx.createdAt,
      });
    }
    if (tx.status === "refunded") {
      events.push({
        type: "payment_refunded",
        message: "Payment was refunded",
        date: tx.updatedAt || tx.createdAt,
      });
    }
    if (tx.status === "pending" || tx.status === "processing") {
      events.push({
        type: "payment_pending",
        message: "Waiting for confirmation",
        date: tx.updatedAt || tx.createdAt,
      });
    }
    events.push({
      type: "transaction_created",
      message: "Transaction created",
      date: tx.createdAt,
    });

    events.sort((a, b) => new Date(b.date) - new Date(a.date));
    return events;
  };

  const getEventIcon = (type) => {
    switch (type) {
      case "payment_succeeded":
        return (
          <div className="tx-activity-icon-wrapper success">
            <CheckCircle size={14} />
          </div>
        );
      case "payment_failed":
        return (
          <div className="tx-activity-icon-wrapper danger">
            <AlertCircle size={14} />
          </div>
        );
      case "payment_refunded":
        return (
          <div className="tx-activity-icon-wrapper warning">
            <ArrowDownLeft size={14} />
          </div>
        );
      case "payment_pending":
        return (
          <div className="tx-activity-icon-wrapper warning">
            <Clock size={14} />
          </div>
        );
      default:
        return (
          <div className="tx-activity-icon-wrapper primary">
            <ArrowUpRight size={14} />
          </div>
        );
    }
  };

  // R-1: client-side CSV export of the currently loaded/filtered rows.
  const escapeCsv = (value) => {
    const str = value == null ? "" : String(value);
    return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
  };

  const handleExportCsv = () => {
    const headers = [
      "Transaction ID",
      "Amount",
      "Currency",
      "Amount (USD)",
      "Status",
      "Type",
      "Method",
      "TX Hash",
      "Description",
      "Date",
    ];
    const rows = transactions.map((tx) => [
      tx.transactionId,
      formatCrypto(tx.amount),
      tx.currency,
      tx.amountUsd != null ? formatUSD(tx.amountUsd) : "",
      getStatusDisplayText(tx.status),
      txKind(tx).label,
      tx.method || "",
      tx.txHash || "",
      tx.description || "",
      formatDate(tx.createdAt),
    ]);

    const csv = [headers, ...rows]
      .map((row) => row.map(escapeCsv).join(","))
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `transactions-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const statusTabs = [
    { id: "all", label: "All", count: stats.all },
    { id: "succeeded", label: "Succeeded", count: stats.succeeded },
    {
      id: "not_supported_token",
      label: "Not Supported",
      count: stats.not_supported_token,
    },
  ];

  // Map a chain key/value onto a human-readable network label for the AssetBadge.
  const getChainLabel = (tx) => tx.network || tx.chain || tx.networkName || "";

  // Describe an aggregator route in one line.
  //
  // `route_metadata.steps` is an ARRAY of step objects, not a count. It was
  // interpolated straight into a template string, so the row rendered
  // "lifi · [object Object],[object Object] steps via" — and trailed off after
  // "via" because the protocol names were never appended at all.
  const describeRoute = (conv) => {
    const provider = conv?.route_provider;
    if (!provider) return "";

    const raw = conv?.route_metadata?.steps;
    const steps = Array.isArray(raw) ? raw : [];
    // Older rows may have stored a plain count rather than the step objects.
    const count = Array.isArray(raw)
      ? raw.length
      : Number.isFinite(raw)
        ? raw
        : 0;

    const parts = [provider];
    if (count > 0) parts.push(`${count} ${count === 1 ? "step" : "steps"}`);

    // `via` names the protocol each hop went through. Deduped and kept in
    // order, because a route can use the same one twice.
    const vias = [...new Set(steps.map((st) => st?.via).filter(Boolean))];
    if (vias.length) parts.push(`via ${vias.join(" → ")}`);

    if (conv?.route_metadata?.mevProtected) parts.push("MEV-protected");
    return parts.join(" · ");
  };

  // The stored value is a slug — 'ethereum-mainnet', 'bsc'. Printing it raw put
  // "ethereum-mainnet" in front of merchants; chainDisplayLabel is the same
  // mapping the rest of the dashboard already uses for chain names.
  const chainLabelOf = (chain) =>
    chainDisplayLabel(chain) || String(chain || "");

  // Status props for the redesigned dot-pill. Mirrors the existing confirmation
  // logic: a succeeded payment still awaiting confirmations shows
  // "Confirming · N/M"; an underpaid/partial row shows "Underpaid".
  const getRowStatusProps = (tx) => {
    const normalized = normalizeTransactionStatus(tx.status);
    if (tx.status === "partially_paid") {
      return { status: "underpaid" };
    }
    if (
      normalized === "succeeded" &&
      tx.confirmationStatus === "pending" &&
      tx.blocksConfirmed !== undefined &&
      tx.requiredConfirmations
    ) {
      return {
        status: "confirming",
        detail: `${tx.blocksConfirmed}/${tx.requiredConfirmations}`,
      };
    }
    if (normalized === "succeeded") return { status: "confirmed" };
    if (normalized === "not_supported_token")
      return { status: "not_supported_token" };
    return { status: normalized };
  };

  // Tabs reflect the real, API-backed filters (do not invent statuses the
  // backend cannot filter on). Counts come from the stats endpoint.
  const entTabs = statusTabs.map((t) => ({
    key: t.id,
    label: t.label,
    count: formatCount(t.count),
  }));

  const headerActions = (
    <>
      <RequireFeature
        feature="csv_pdf_export"
        fallback={
          <button
            className="ent-btn ent-btn-secondary"
            onClick={() => setShowExportUpsell(true)}
            title="Export is a Premium feature"
            aria-label="Export transactions (Premium)"
          >
            <Download size={16} />
            Export
          </button>
        }
      >
        <button
          className="ent-btn ent-btn-secondary"
          onClick={handleExportCsv}
          disabled={transactions.length === 0}
          title="Export transactions to CSV"
          aria-label="Export transactions to CSV"
        >
          <Download size={16} />
          Export
        </button>
      </RequireFeature>
      <button
        className="ent-btn ent-btn-primary"
        onClick={() => navigate("/dashboard/invoices")}
      >
        <Plus size={16} />
        New payment link
      </button>
    </>
  );

  return (
    <div className="ent ent-page transactions-page">
      <PageHeaderPro
        title="Transactions"
        mode={mode === "test" ? "test" : "live"}
        updatedText={`Updated ${isLoading ? "now" : "just now"}`}
        actions={headerActions}
      />

      {/* Headline figures, top assets and daily activity. Server-computed:
          this page holds one page of rows, so a period total or a
          "vs. previous period" delta cannot be derived from what it has. */}
      <TransactionSummary
        token={token}
        mode={mode}
        filters={{
          type: typeFilter,
          search: debouncedSearchQuery,
          merchantId: isSuperAdmin ? merchantFilter : undefined,
          network: advancedFilters.network,
          currency: advancedFilters.currency,
        }}
      />

      <FilterTabs
        tabs={entTabs}
        active={statusFilter}
        onChange={handleStatusFilter}
        right={
          <EntSearch
            value={searchQuery}
            onChange={(v) => {
              setSearchQuery(v);
              setCurrentPage(1);
            }}
            placeholder="Search by hash, email, amount…"
          />
        }
      />

      <div className="table-toolbar table-toolbar--secondary">
        <div className="toolbar-actions">
          <div className="dropdown-container" ref={filterDropdownRef}>
            <button
              className={`btn-toolbar ${statusFilter !== "all" || typeFilter !== "all" ? "is-filtered" : ""}`}
              onClick={() => setShowFilterDropdown(!showFilterDropdown)}
            >
              <Filter size={16} />
              Quick Filters
              {(statusFilter !== "all" ? 1 : 0) +
                (typeFilter !== "all" ? 1 : 0) >
                0 && (
                <span className="qf-badge">
                  {(statusFilter !== "all" ? 1 : 0) +
                    (typeFilter !== "all" ? 1 : 0)}
                </span>
              )}
            </button>
            {showFilterDropdown && (
              <div className="dropdown-menu filter-dropdown">
                {/* Says what is on and offers the way out. Without it the only
                    clue a filter is active is a tick further down the list. */}
                <div className="qf-top">
                  <span className="qf-top-title">Filters</span>
                  {(statusFilter !== "all" || typeFilter !== "all") && (
                    <button
                      type="button"
                      className="qf-reset"
                      onClick={() => {
                        handleStatusFilter("all");
                        setTypeFilter("all");
                        setCurrentPage(1);
                      }}
                    >
                      Reset
                    </button>
                  )}
                </div>
                <div className="dropdown-section">
                  <div className="dropdown-header">Status</div>
                  {statusTabs.map((tab) => (
                    <button
                      key={tab.id}
                      className={`dropdown-item qf-item ${statusFilter === tab.id ? "active" : ""}`}
                      onClick={() => {
                        handleStatusFilter(tab.id);
                      }}
                    >
                      <span className="qf-check">
                        {statusFilter === tab.id && <Check size={14} />}
                      </span>
                      <span className="qf-label">{tab.label}</span>
                      <span className="dropdown-count">
                        {formatCount(tab.count)}
                      </span>
                    </button>
                  ))}
                </div>
                <div className="dropdown-divider" />
                <div className="dropdown-section">
                  <div className="dropdown-header">Type</div>
                  {[
                    // Matches the TYPE column: what the payment was raised
                    // against, not the row's stored type. "Payment" is gone as
                    // an option because every incoming row is one, so it
                    // filtered nothing out.
                    { id: "all", label: "All types", icon: Inbox },
                    { id: "invoice", label: "Invoice", icon: FileText },
                    { id: "order", label: "Order", icon: Wallet },
                    {
                      id: "untracked",
                      label: "Untracked",
                      icon: AlertCircle,
                      hint: "No invoice or order",
                    },
                    { id: "payout", label: "Payout", icon: ArrowUpRight },
                    // { id: "refund", label: "Refund" },
                  ].map((type) => {
                    const Icon = type.icon;
                    const active = typeFilter === type.id;
                    return (
                      <button
                        key={type.id}
                        className={`dropdown-item qf-item ${active ? "active" : ""}`}
                        onClick={() => {
                          setTypeFilter(type.id);
                          setCurrentPage(1);
                        }}
                        title={type.hint || undefined}
                      >
                        {/* The tick sits in its own fixed slot so selecting an
                            item does not shove every label sideways. */}
                        <span className="qf-check">
                          {active && <Check size={14} />}
                        </span>
                        <Icon size={15} className="qf-icon" />
                        <span className="qf-label">{type.label}</span>
                      </button>
                    );
                  })}
                </div>
                {isSuperAdmin && (
                  <>
                    <div className="dropdown-divider" />
                    <div className="dropdown-section">
                      <div className="dropdown-header">Merchant</div>
                      <button
                        className={`dropdown-item ${merchantFilter === "all" ? "active" : ""}`}
                        onClick={() => {
                          setMerchantFilter("all");
                          setCurrentPage(1);
                        }}
                      >
                        {merchantFilter === "all" && <Check size={14} />}
                        All Merchants
                      </button>
                      {isMerchantsLoading ? (
                        <div className="dropdown-item disabled">Loading...</div>
                      ) : (
                        merchants.map((merchant) => (
                          <button
                            key={merchant.id}
                            className={`dropdown-item ${merchantFilter === merchant.id.toString() ? "active" : ""}`}
                            onClick={() => {
                              setMerchantFilter(merchant.id.toString());
                              setCurrentPage(1);
                            }}
                          >
                            {merchantFilter === merchant.id.toString() && (
                              <Check size={14} />
                            )}
                            <div className="merchant-filter-item">
                              <span>
                                {merchant.businessName ||
                                  `${merchant.firstName} ${merchant.lastName}`}
                              </span>
                              <span className="merchant-email-hint">
                                {merchant.email}
                              </span>
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          <div className="dropdown-container" ref={columnDropdownRef}>
            <button
              className="btn-toolbar"
              onClick={() => setShowColumnDropdown(!showColumnDropdown)}
            >
              <Columns size={16} />
              Columns
            </button>
            {showColumnDropdown && (
              <div className="dropdown-menu column-dropdown">
                <div className="dropdown-header">
                  Show/Hide Columns
                  <button
                    className="reset-columns-btn"
                    onClick={() =>
                      setVisibleColumns({
                        status: true,
                        transaction: true,
                        type: true,
                        payment: true,
                        asset: true,
                        amount: true,
                        date: true,
                      })
                    }
                  >
                    Reset
                  </button>
                </div>
                {Object.entries(visibleColumns).map(([key, visible]) => {
                  const isLocked = LOCKED_COLUMNS.includes(key);
                  return (
                    <label
                      key={key}
                      className={`dropdown-checkbox${isLocked ? " is-locked" : ""}`}
                      title={
                        isLocked ? "This column is always shown" : undefined
                      }
                    >
                      <input
                        type="checkbox"
                        checked={visible}
                        disabled={isLocked}
                        onChange={() => {
                          if (isLocked) return;
                          setVisibleColumns((prev) => ({
                            ...prev,
                            [key]: !prev[key],
                          }));
                        }}
                      />
                      <span className="checkbox-custom">
                        {visible && <Check size={12} />}
                      </span>
                      {key.charAt(0).toUpperCase() + key.slice(1)}
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          {(statusFilter !== "all" ||
            typeFilter !== "all" ||
            searchQuery ||
            getActiveFiltersCount() > 0) && (
            <button className="btn-clear-filters" onClick={clearAllFilters}>
              <X size={14} />
              Clear all
            </button>
          )}
        </div>
      </div>

      <div
        className={`transactions-table-container tx-fade ${isRefetching ? "is-refetching" : ""}`}
        aria-busy={isRefetching || isLoading}
      >
        {isLoading ? (
          <TableSkeleton rows={pageSize > 10 ? 10 : pageSize} cols={6} />
        ) : fetchError ? (
          <EmptyState
            icon={<AlertTriangle size={24} />}
            title="Could not load transactions"
            subtitle="Something went wrong while fetching your transactions."
            action={{ label: "Retry", onClick: fetchTransactions }}
          />
        ) : transactions.length === 0 ? (
          <EmptyState
            icon={<Inbox size={24} />}
            title="No transactions yet"
            subtitle="Your transaction history will appear here."
          />
        ) : isMobile ? (
          /* Mobile card list — replaces the table at <=576px */
          /* Keyed on the filter set so React remounts it and the fade-in plays
             again; without a key the same node is reused and the animation only
             ever runs once. */
          <div
            className="tx-card-list tx-results"
            key={`m-${statusFilter}-${typeFilter}-${currentPage}`}
          >
            {transactions.map((tx) => (
              <div
                key={tx.id}
                className="tx-row-card"
                onClick={() => setSelectedTransaction(tx)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) =>
                  e.key === "Enter" && setSelectedTransaction(tx)
                }
              >
                <div className="tx-row-card-top">
                  <span className="tx-row-card-amount">
                    {formatAmountLocal(tx.amount, tx.currency, tx.amountUsd)}
                  </span>
                  <span
                    className="tx-row-card-top-badges"
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      justifyContent: "flex-end",
                      gap: 6,
                    }}
                  >
                    <StatusBadge
                      status={normalizeTransactionStatus(tx.status)}
                    />
                    {tx.paymentStatus && (
                      <StatusBadge
                        status={tx.paymentStatus}
                        label={formatPaymentStatusDisplay(tx.paymentStatus)}
                        dot
                      />
                    )}
                  </span>
                </div>
                <div className="tx-row-card-bottom">
                  <span className="tx-row-card-meta">
                    <span className="tx-row-card-type">{txKind(tx).label}</span>
                    {tx.method && (
                      <>
                        <span>·</span>
                        <span>{tx.method}</span>
                      </>
                    )}
                  </span>
                  <span className="tx-row-card-date">
                    {formatDate(tx.createdAt)}
                  </span>
                </div>
                {tx.txHash && (
                  <div className="tx-row-card-hash">
                    {tx.txHash.slice(0, 10)}…{tx.txHash.slice(-8)}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          /* Keyed on the filter set so the fade-in replays on each change;
             React would otherwise reuse the node and the animation would run
             only on first mount. The header stays put — only the body of the
             result is what changed. */
          <div
            className="pro-table-wrap tx-results"
            key={`d-${statusFilter}-${typeFilter}-${currentPage}`}
          >
            <table className="pro-table">
              <thead>
                <tr>
                  {visibleColumns.status && <th>STATUS</th>}
                  {visibleColumns.transaction && (
                    <th
                      onClick={() => handleSort("created_at")}
                      className="sortable"
                    >
                      TRANSACTION
                    </th>
                  )}
                  {visibleColumns.type && (
                    <th onClick={() => handleSort("type")} className="sortable">
                      TYPE
                    </th>
                  )}
                  {visibleColumns.payment && <th>PAYMENT</th>}
                  {visibleColumns.asset && <th>ASSET</th>}
                  {visibleColumns.amount && (
                    <th
                      className="amount-cell sortable"
                      onClick={() => handleSort("amount")}
                    >
                      AMOUNT
                    </th>
                  )}
                  {visibleColumns.date && (
                    <th
                      onClick={() => handleSort("created_at")}
                      className="sortable"
                    >
                      DATE
                    </th>
                  )}
                  <th className="col-kebab" aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {transactions.map((tx) => {
                  const statusProps = getRowStatusProps(tx);
                  return (
                    <tr key={tx.id} onClick={() => setSelectedTransaction(tx)}>
                      {visibleColumns.status && (
                        <td data-label="STATUS">
                          <StatusBadge {...statusProps} dot />
                          {/* TXN-03: flag genuine zero-value succeeded payments */}
                          {isZeroValueAnomaly(tx) && (
                            <span
                              className="status-badge status-anomaly"
                              aria-label="Zero-value payment flagged as anomaly"
                              title="Zero-value payment flagged as anomaly"
                            >
                              <AlertTriangle size={12} />
                            </span>
                          )}
                        </td>
                      )}
                      {visibleColumns.transaction && (
                        <td data-label="TRANSACTION">
                          <span className="mono">
                            {tx.txHash
                              ? truncateHash(tx.txHash, 10, 3)
                              : tx.transactionId}
                          </span>
                        </td>
                      )}
                      {visibleColumns.type && (
                        <td data-label="TYPE">
                          {(() => {
                            const kind = txKind(tx);
                            return (
                              <span
                                className={`tx-type-pill tx-type-pill--${kind.tone}`}
                                title={
                                  kind.label === "Untracked"
                                    ? "Received with no invoice or order behind it"
                                    : undefined
                                }
                              >
                                {kind.label}
                              </span>
                            );
                          })()}
                        </td>
                      )}
                      {visibleColumns.payment && (
                        <td data-label="PAYMENT">
                          {tx.paymentStatus ? (
                            <StatusBadge
                              status={tx.paymentStatus}
                              label={formatPaymentStatusDisplay(
                                tx.paymentStatus,
                              )}
                              dot
                            />
                          ) : (
                            "—"
                          )}
                        </td>
                      )}
                      {visibleColumns.asset && (
                        <td data-label="ASSET">
                          <AssetBadge
                            symbol={tx.currency}
                            chain={getChainLabel(tx)}
                          />
                        </td>
                      )}
                      {visibleColumns.amount && (
                        <td className="amount-cell" data-label="AMOUNT">
                          <span className="amount-cell__main">
                            {tx.amountUsd != null
                              ? `$${formatUSD(tx.amountUsd)}`
                              : `$${formatUSD(tx.amount)}`}
                          </span>
                          <span className="amount-cell__sub">
                            {formatCrypto(tx.amount)} {tx.currency}
                          </span>
                        </td>
                      )}
                      {visibleColumns.date && (
                        <td data-label="DATE">{formatDate(tx.createdAt)}</td>
                      )}
                      <td
                        className="col-kebab"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          className="row-kebab"
                          onClick={() => setSelectedTransaction(tx)}
                          aria-label="Transaction actions"
                        >
                          <MoreVertical size={16} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {transactions.length > 0 && (
        <div className="tx-pager-row">
          <Pager
            page={currentPage}
            pageCount={totalPages}
            total={totalItems}
            pageSize={pageSize}
            onPage={setCurrentPage}
          />
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setCurrentPage(1);
            }}
            className="page-size-select"
          >
            <option value={10}>10 per page</option>
            <option value={25}>25 per page</option>
            <option value={50}>50 per page</option>
            <option value={100}>100 per page</option>
          </select>
        </div>
      )}

      {selectedTransaction && (
        <>
          <div
            className="tx-drawer-overlay"
            onClick={() => setSelectedTransaction(null)}
          />
          <div className="tx-detail-drawer">
            <div className="tx-drawer-header">
              <div className="tx-drawer-header-left">
                <button
                  className="tx-close-btn"
                  onClick={() => setSelectedTransaction(null)}
                >
                  <X size={20} />
                </button>
                <div className="tx-drawer-title-section">
                  <div className="tx-drawer-icon-wrapper">
                    {selectedTransaction.type === "invoice" ? (
                      <FileText size={18} />
                    ) : (
                      <Wallet size={18} />
                    )}
                  </div>
                  <div>
                    <h2>Transaction Details</h2>
                    <span className="tx-id-text">
                      {selectedTransaction.transactionId}
                    </span>
                  </div>
                </div>
              </div>
              <div className="tx-drawer-header-right">
                {explorerTxUrl(
                  selectedTransaction.networkUrl,
                  selectedTransaction.network,
                  selectedTransaction.txHash,
                ) && (
                  <a
                    className="tx-btn-outline"
                    href={explorerTxUrl(
                      selectedTransaction.networkUrl,
                      selectedTransaction.network,
                      selectedTransaction.txHash,
                    )}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <ExternalLink size={16} />
                    View on Explorer
                  </a>
                )}
              </div>
            </div>

            <div className="tx-drawer-content">
              <div className="tx-title-section">
                <div className="tx-title-left">
                  <h1>
                    {formatAmountLocal(
                      selectedTransaction.amount,
                      selectedTransaction.currency,
                      selectedTransaction.amountUsd,
                    )}
                  </h1>
                  <StatusBadge
                    status={normalizeTransactionStatus(
                      selectedTransaction.status,
                    )}
                  />
                </div>
                {selectedTransaction.currency && (
                  <div className="tx-title-asset">
                    <AssetBadge
                      symbol={selectedTransaction.currency}
                      chain={getChainLabel(selectedTransaction)}
                    />
                  </div>
                )}
              </div>

              <p className="tx-subtitle-text">
                {selectedTransaction.invoiceId
                  ? `Invoice ${selectedTransaction.invoiceId}`
                  : txKind(selectedTransaction).label}
                {selectedTransaction.customerName &&
                  ` · ${selectedTransaction.customerName}`}
              </p>

              <div className="tx-id-bar">
                <Wallet size={16} className="tx-id-icon" />
                <input
                  type="text"
                  value={selectedTransaction.transactionId}
                  readOnly
                  className="tx-id-input"
                />
                <button
                  className="tx-copy-btn"
                  onClick={() =>
                    handleCopyTxId(selectedTransaction.transactionId)
                  }
                  title="Copy Transaction ID"
                >
                  {copiedTxId ? <CheckCircle size={16} /> : <Copy size={16} />}
                </button>
              </div>

              <div className="tx-card">
                <div className="tx-card-header-row">
                  <h3>Recent activity</h3>
                </div>
                <div className="tx-activity-list">
                  {generateTransactionEvents(selectedTransaction)
                    .slice(0, 3)
                    .map((event, index) => (
                      <div key={index} className="tx-activity-item">
                        {getEventIcon(event.type)}
                        <div className="tx-activity-content">
                          <span className="tx-activity-message">
                            {event.message}
                          </span>
                          <span className="tx-activity-date">
                            {formatDate(event.date)}
                          </span>
                        </div>
                      </div>
                    ))}
                </div>
              </div>

              {selectedTransaction.status === "succeeded" &&
                selectedTransaction.requiredConfirmations && (
                  <div className="tx-card tx-confirmation-card">
                    <div className="tx-card-header-row">
                      <h3>
                        <Shield size={16} className="tx-card-icon" />
                        Block Confirmation
                      </h3>
                      <span
                        className={`tx-confirmation-badge ${getConfirmationBadgeClass(selectedTransaction.confirmationStatus)}`}
                      >
                        {selectedTransaction.confirmationStatus ===
                          "pending" && (
                          <Loader2 size={12} className="spinning" />
                        )}
                        {selectedTransaction.confirmationStatus ===
                          "confirmed" && <CheckCircle size={12} />}
                        {(selectedTransaction.confirmationStatus === "failed" ||
                          selectedTransaction.reorged) && (
                          <AlertCircle size={12} />
                        )}
                        {getConfirmationDisplayText(selectedTransaction)}
                      </span>
                    </div>

                    <div className="tx-confirmation-content">
                      {selectedTransaction.confirmationStatus === "pending" ? (
                        <div className="tx-confirmation-progress">
                          <div className="tx-confirmation-info">
                            <span className="tx-confirmation-label">
                              Transaction successful
                            </span>
                            <span className="tx-confirmation-sublabel">
                              Processing & validating on{" "}
                              {selectedTransaction.network}
                            </span>
                          </div>
                          <div className="tx-confirmation-bar-container">
                            <div
                              className="tx-confirmation-bar"
                              style={{
                                width: `${Math.min(100, ((selectedTransaction.blocksConfirmed || 0) / selectedTransaction.requiredConfirmations) * 100)}%`,
                              }}
                            />
                          </div>
                          <div className="tx-confirmation-counts">
                            <span>
                              {selectedTransaction.blocksConfirmed || 0} of{" "}
                              {selectedTransaction.requiredConfirmations}{" "}
                              confirmations
                            </span>
                            <span className="tx-confirmation-remaining">
                              {Math.max(
                                0,
                                selectedTransaction.requiredConfirmations -
                                  (selectedTransaction.blocksConfirmed || 0),
                              )}{" "}
                              remaining
                            </span>
                          </div>
                        </div>
                      ) : selectedTransaction.confirmationStatus ===
                        "confirmed" ? (
                        <div className="tx-confirmation-complete">
                          <div className="tx-confirmation-success-icon">
                            <CheckCircle size={24} />
                          </div>
                          <div className="tx-confirmation-success-text">
                            <span className="tx-confirmation-label">
                              Transaction confirmed
                            </span>
                            <span className="tx-confirmation-sublabel">
                              {selectedTransaction.blocksConfirmed} block
                              confirmations reached
                            </span>
                            {selectedTransaction.confirmedAt && (
                              <span className="tx-confirmation-time">
                                Confirmed at{" "}
                                {formatDate(selectedTransaction.confirmedAt)}
                              </span>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="tx-confirmation-failed">
                          <div className="tx-confirmation-failed-icon">
                            <AlertCircle size={24} />
                          </div>
                          <div className="tx-confirmation-failed-text">
                            <span className="tx-confirmation-label">
                              Transaction failed during validation
                            </span>
                            <span className="tx-confirmation-sublabel">
                              {selectedTransaction.reorged
                                ? "Transaction was removed from the blockchain (reorg)"
                                : "Confirmation process failed"}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>

                    {selectedTransaction.webhookSent && (
                      <div className="tx-webhook-status">
                        <CheckCircle size={14} />
                        <span>Webhook sent to merchant</span>
                        {selectedTransaction.webhookSentAt && (
                          <span className="tx-webhook-time">
                            {formatDate(selectedTransaction.webhookSentAt)}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                )}

              <div className="tx-card">
                <h3>Summary</h3>
                <div className="tx-summary-grid">
                  <div className="tx-summary-left">
                    {(selectedTransaction.customerName ||
                      selectedTransaction.customerEmail) && (
                      <div className="tx-summary-field">
                        <label>Customer</label>
                        <div className="tx-summary-value">
                          {selectedTransaction.customerName && (
                            <span>{selectedTransaction.customerName}</span>
                          )}
                          {selectedTransaction.customerEmail && (
                            <span className="tx-summary-subtext">
                              {selectedTransaction.customerEmail}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                    {selectedTransaction.description && (
                      <div className="tx-summary-field">
                        <label>Description</label>
                        <span className="tx-summary-value">
                          {selectedTransaction.description}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="tx-summary-right">
                    <div className="tx-summary-field">
                      <label>Transaction ID</label>
                      <span className="tx-summary-value tx-mono">
                        {selectedTransaction.transactionId}
                      </span>
                    </div>
                    <div className="tx-summary-field">
                      <label>Type</label>
                      <span className="tx-summary-value">
                        {txKind(selectedTransaction).label}
                      </span>
                    </div>
                    {selectedTransaction.method && (
                      <div className="tx-summary-field">
                        <label>Payment Method</label>
                        <span className="tx-summary-value">
                          {selectedTransaction.method}
                        </span>
                      </div>
                    )}
                    {selectedTransaction.network && (
                      <div className="tx-summary-field">
                        <label>Network</label>
                        <span className="tx-summary-value tx-summary-value--asset">
                          <ChainLogo
                            chain={selectedTransaction.network}
                            size={18}
                          />
                          <span>
                            {chainLabelOf(
                              getChainLabel(selectedTransaction) ||
                                selectedTransaction.network,
                            )}
                          </span>
                        </span>
                      </div>
                    )}
                    {/* TXN-01: Chain ID. Rendered only when present on the tx object.
                        TODO: backend may not yet return `chainId` on transactions. */}
                    {selectedTransaction.chainId != null &&
                      selectedTransaction.chainId !== "" && (
                        <div className="tx-summary-field">
                          <label>Chain ID</label>
                          <span className="tx-summary-value tx-mono">
                            {selectedTransaction.chainId}
                          </span>
                        </div>
                      )}
                    {/* TXN-01: dedicated Token field. Prefer `tokenSymbol`, fall
                        back to `currency` which is present on the tx object.
                        TODO: backend may not yet return a dedicated `tokenSymbol`. */}
                    {(selectedTransaction.tokenSymbol ||
                      selectedTransaction.currency) && (
                      <div className="tx-summary-field">
                        <label>Token</label>
                        <span className="tx-summary-value tx-summary-value--asset">
                          <TokenLogo
                            symbol={
                              selectedTransaction.tokenSymbol ||
                              selectedTransaction.currency
                            }
                            size={18}
                          />
                          <span>
                            {selectedTransaction.tokenSymbol ||
                              selectedTransaction.currency}
                          </span>
                        </span>
                      </div>
                    )}
                    {/* TXN-01: link to the related invoice when one exists. */}
                    {selectedTransaction.invoiceId && (
                      <div className="tx-summary-field">
                        <label>Invoice</label>
                        <Link
                          className="tx-summary-value tx-summary-link"
                          to={`/dashboard/invoices/${selectedTransaction.invoiceId}`}
                          onClick={() => setSelectedTransaction(null)}
                        >
                          View invoice
                        </Link>
                      </div>
                    )}
                    <div className="tx-summary-field">
                      <label>Date</label>
                      <span className="tx-summary-value">
                        {formatDate(selectedTransaction.createdAt)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* What was received, in what, and what it settled as.
                  "Total" used to repeat "Amount" verbatim — there is no fee
                  breakdown on a received payment, so the two could never differ
                  and the row said nothing. It is replaced by the settlement
                  line, which is the question this card was failing to answer. */}
              <div className="tx-card">
                <h3>Amount Details</h3>
                <div className="tx-totals-block">
                  <div className="tx-total-row tx-total-main">
                    <span>Received</span>
                    <span className="tx-amount-asset">
                      <TokenLogo
                        symbol={
                          selectedTransaction.tokenSymbol ||
                          selectedTransaction.currency
                        }
                        size={18}
                      />
                      {formatAmountLocal(
                        selectedTransaction.amount,
                        selectedTransaction.currency,
                      )}
                    </span>
                  </div>

                  {selectedTransaction.amountUsd != null &&
                    String(selectedTransaction.currency).toUpperCase() !==
                      "USD" && (
                      <div className="tx-total-row">
                        <span>Value at the time</span>
                        <span>${formatUSD(selectedTransaction.amountUsd)}</span>
                      </div>
                    )}

                  {selectedTransaction.network && (
                    <div className="tx-total-row">
                      <span>Received on</span>
                      <span className="tx-amount-asset">
                        <ChainLogo
                          chain={selectedTransaction.network}
                          size={18}
                        />
                        {chainLabelOf(
                          getChainLabel(selectedTransaction) ||
                            selectedTransaction.network,
                        )}
                      </span>
                    </div>
                  )}

                  {/* Both sides of a conversion are named explicitly: what the
                      customer paid in, and what it became. Showing only the
                      destination left a credit leg reading "USDT" with no hint
                      that the USDT arrived from a different chain entirely.

                      Whether a conversion happened at all is stated either way —
                      rendering nothing when there is none left the merchant
                      unable to tell "not converted" from "we did not say". */}
                  {conversion ? (
                    <>
                      <div className="tx-total-row tx-settles-row">
                        <span>Paid in</span>
                        <span className="tx-amount-asset">
                          <TokenChainLogo
                            symbol={conversion.source_asset}
                            chain={conversion.source_chain}
                            size={18}
                          />
                          {formatCrypto(conversion.source_amount)}{" "}
                          {conversion.source_asset}
                          <span className="tx-settles-chain">
                            on {chainLabelOf(conversion.source_chain)}
                          </span>
                        </span>
                      </div>
                      <div className="tx-total-row tx-settles-row tx-settles-row--to">
                        <span>
                          {conversion.status === "completed"
                            ? "Converted to"
                            : "Converting to"}
                        </span>
                        <span className="tx-amount-asset">
                          {conversion.status === "completed" ? (
                            <>
                              <TokenChainLogo
                                symbol={conversion.target_asset}
                                chain={conversion.target_chain}
                                size={18}
                              />
                              {formatCrypto(conversion.target_amount)}{" "}
                              {conversion.target_asset}
                              <span className="tx-settles-chain">
                                on {chainLabelOf(conversion.target_chain)}
                              </span>
                              <em className="tx-settles-note">
                                auto-converted
                              </em>
                            </>
                          ) : (
                            <>
                              <Clock size={14} />
                              {conversion.target_asset} on{" "}
                              {chainLabelOf(conversion.target_chain)}
                              <em className="tx-settles-note">in progress</em>
                            </>
                          )}
                        </span>
                      </div>
                    </>
                  ) : (
                    <div className="tx-total-row tx-settles-row">
                      <span>Settles as</span>
                      <span className="tx-amount-asset">
                        <TokenLogo
                          symbol={
                            selectedTransaction.tokenSymbol ||
                            selectedTransaction.currency
                          }
                          size={18}
                        />
                        {selectedTransaction.tokenSymbol ||
                          selectedTransaction.currency}
                        <em className="tx-settles-note">not converted</em>
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* PRD §3.2: Auto-conversion audit trail. Renders nothing when
                  there is no conversion for this payment. */}
              {conversion &&
                (() => {
                  const isCompleted = conversion.status === "completed";
                  // The hash shown and the explorer it opens must be on the
                  // same chain. tx_hash_dest is where the converted asset
                  // landed -- the TARGET chain (Ethereum for a BNB -> ETH
                  // conversion) -- so it used to open on the payment's own
                  // explorer (BscScan) and land on "transaction not found".
                  const onTarget = Boolean(conversion.tx_hash_dest);
                  const hash = onTarget
                    ? conversion.tx_hash_dest
                    : conversion.tx_hash_source;
                  const conversionLink = onTarget
                    ? explorerTxUrl(
                        conversion.target_explorer_url,
                        conversion.target_network_slug || conversion.target_chain,
                        hash,
                      )
                    : explorerTxUrl(
                        conversion.source_explorer_url,
                        conversion.source_network_slug || conversion.source_chain,
                        hash,
                      );
                  const pendingLabel =
                    CONVERSION_PENDING_LABELS[conversion.status] ||
                    "Conversion pending";
                  return (
                    <div className="tx-card tx-conversion-card">
                      <div className="tx-card-header-row">
                        <h3>
                          <Repeat size={16} className="tx-card-icon" />
                          Conversion
                        </h3>
                        {!isCompleted && (
                          <span className="tx-conversion-pill">
                            <Clock size={12} />
                            {pendingLabel}
                          </span>
                        )}
                      </div>

                      <div className="tx-conversion-flow">
                        <div className="tx-conversion-leg">
                          <TokenChainLogo
                            symbol={conversion.source_asset}
                            chain={conversion.source_chain}
                            size={28}
                          />
                          <div className="tx-conversion-leg-text">
                            <span className="tx-conversion-leg-label">
                              Customer paid
                            </span>
                            <span className="tx-conversion-leg-value">
                              {formatCrypto(conversion.source_amount)}{" "}
                              {conversion.source_asset} on{" "}
                              {conversion.source_chain}
                            </span>
                          </div>
                        </div>

                        {isCompleted ? (
                          <>
                            <div className="tx-conversion-leg">
                              <TokenChainLogo
                                symbol={conversion.target_asset}
                                chain={conversion.target_chain}
                                size={28}
                              />
                              <div className="tx-conversion-leg-text">
                                <span className="tx-conversion-leg-label">
                                  Auto-converted to
                                </span>
                                <span className="tx-conversion-leg-value">
                                  {formatCrypto(conversion.target_amount)}{" "}
                                  {conversion.target_asset} on{" "}
                                  {conversion.target_chain}
                                </span>
                              </div>
                            </div>
                            <div className="tx-conversion-net">
                              <span>Net credited</span>
                              <strong>
                                {formatCrypto(conversion.target_amount)}{" "}
                                {conversion.target_asset}
                              </strong>
                            </div>
                          </>
                        ) : (
                          <p className="tx-subtitle-text tx-conversion-pending-note">
                            Converting to {conversion.target_asset} on{" "}
                            {conversion.target_chain}. Final amounts appear once
                            settlement completes.
                          </p>
                        )}
                      </div>

                      <div className="tx-conversion-details">
                        {conversion.route_provider && (
                          <div className="tx-total-row">
                            <span>Route</span>
                            <span className="tx-route-value">
                              {describeRoute(conversion)}
                            </span>
                          </div>
                        )}
                        {conversion.network_gas_usd != null && (
                          <div className="tx-total-row">
                            <span>Network fee</span>
                            <span>
                              ${formatUSD(conversion.network_gas_usd)}
                            </span>
                          </div>
                        )}
                        {conversion.aggregator_fee_usd != null && (
                          <div className="tx-total-row">
                            <span>Aggregator fee</span>
                            <span>
                              ${formatUSD(conversion.aggregator_fee_usd)}
                            </span>
                          </div>
                        )}
                        {conversion.maxelpay_fee_usd != null && (
                          <div className="tx-total-row">
                            <span>MaxelPay fee</span>
                            <span>
                              ${formatUSD(conversion.maxelpay_fee_usd)}
                            </span>
                          </div>
                        )}
                        {conversion.route_metadata?.estTimeSec != null && (
                          <div className="tx-total-row">
                            <span>Execution time</span>
                            <span>{conversion.route_metadata.estTimeSec}s</span>
                          </div>
                        )}
                        {hash && (
                          <div className="tx-total-row">
                            <span>Conversion tx hash</span>
                            <span className="tx-conversion-hash">
                              <span className="tx-mono">
                                {truncateHash(hash, 10, 3)}
                              </span>
                              {conversionLink && (
                                <a
                                  className="tx-copy-btn-small"
                                  href={conversionLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  aria-label="View conversion on explorer"
                                >
                                  <ExternalLink size={14} />
                                </a>
                              )}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}

              {(selectedTransaction.txHash ||
                selectedTransaction.toAddress ||
                selectedTransaction.fromAddress) && (
                <div className="tx-card">
                  <h3>Blockchain Details</h3>
                  <div className="tx-blockchain-details">
                    {selectedTransaction.txHash && (
                      <div className="tx-blockchain-row">
                        <label>Transaction Hash</label>
                        <div className="tx-hash-value">
                          <span className="tx-hash-text">
                            {selectedTransaction.txHash}
                          </span>
                          <button
                            className="tx-copy-btn-small"
                            onClick={() =>
                              handleCopyTxHash(selectedTransaction.txHash)
                            }
                          >
                            {copiedTxHash ? (
                              <CheckCircle size={14} />
                            ) : (
                              <Copy size={14} />
                            )}
                          </button>
                          {explorerTxUrl(
                            selectedTransaction.networkUrl,
                            selectedTransaction.network,
                            selectedTransaction.txHash,
                          ) && (
                            <a
                              className="tx-copy-btn-small"
                              href={explorerTxUrl(
                                selectedTransaction.networkUrl,
                                selectedTransaction.network,
                                selectedTransaction.txHash,
                              )}
                              target="_blank"
                              rel="noopener noreferrer"
                              aria-label="View transaction on explorer"
                            >
                              <ExternalLink size={14} />
                            </a>
                          )}
                        </div>
                      </div>
                    )}
                    {/* {selectedTransaction.fromAddress && (
                      <div className="tx-blockchain-row">
                        <label>From Address</label>
                        <span className="tx-address-text">
                          {selectedTransaction.fromAddress}
                        </span>
                      </div>
                    )}
                    {selectedTransaction.toAddress && (
                      <div className="tx-blockchain-row">
                        <label>To Address</label>
                        <span className="tx-address-text">
                          {selectedTransaction.toAddress}
                        </span>
                      </div>
                    )} */}
                  </div>
                </div>
              )}

              <div className="tx-card">
                <h3>Events</h3>
                <div className="tx-events-list">
                  {generateTransactionEvents(selectedTransaction).map(
                    (event, index) => (
                      <div key={index} className="tx-event-row">
                        <span className="tx-event-text">{event.message}</span>
                        <span className="tx-event-time">
                          {formatDate(event.date)}
                        </span>
                      </div>
                    ),
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {showExportUpsell && (
        <UpsellModal
          feature="csv_pdf_export"
          onClose={() => setShowExportUpsell(false)}
          onUpgrade={() => navigate("/dashboard/settings?tab=billing")}
        />
      )}

      <AdvancedFilterModal
        isOpen={showAdvancedFilters}
        onClose={() => setShowAdvancedFilters(false)}
        onApply={handleApplyAdvancedFilters}
        filters={advancedFilters}
        sortConfig={{ sortBy, sortOrder }}
        filterOptions={[
          {
            key: "network",
            label: "Network",
            type: "select",
            options: [
              { value: "ethereum", label: "Ethereum" },
              { value: "ethereum-sepolia", label: "Ethereum Sepolia" },
              { value: "polygon", label: "Polygon" },
              { value: "arbitrum", label: "Arbitrum" },
              { value: "bsc", label: "BSC" },
            ],
          },
          {
            key: "currency",
            label: "Currency",
            type: "select",
            options: [
              { value: "USDT", label: "USDT" },
              { value: "USDC", label: "USDC" },
              { value: "ETH", label: "ETH" },
              { value: "USD", label: "USD" },
              { value: "TRX", label: "TRX" },
            ],
          },
          {
            key: "amount",
            label: "Amount Range",
            type: "range",
          },
        ]}
        sortOptions={[
          { value: "created_at", label: "Date Created" },
          { value: "amount", label: "Amount" },
          { value: "status", label: "Status" },
          { value: "type", label: "Type" },
        ]}
        title="Advanced Filters"
      />

      <style>{`
        /* Merchant Filter Styles */
        .merchant-filter-item {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 2px;
        }

        .merchant-filter-item span:first-child {
          font-weight: 500;
        }

        .merchant-email-hint {
          font-size: 0.75rem;
          color: var(--t-500);
        }

        .dropdown-item.disabled {
          opacity: 0.6;
          cursor: not-allowed;
          pointer-events: none;
        }

        /* Payment type pill in the transactions table */
        /* ── Quick Filters ──────────────────────────────────────────── */
        /* Scoped to this dropdown: .dropdown-item is shared with the header
           menus and must keep its own look there. */
        .filter-dropdown {
          min-width: 250px;
          padding: 0;
          border-radius: var(--r-md, 10px);
          box-shadow: 0 12px 32px rgba(14, 17, 36, 0.14);
          overflow-y: scroll;
          height:40vh;
        }
        .qf-top {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 14px;
          border-bottom: 1px solid var(--line);
          background: var(--bg-inset);
        }
        .qf-top-title {
          font-size: 12.5px;
          font-weight: 700;
          color: var(--ink);
        }
        .qf-reset {
          border: none;
          background: none;
          padding: 2px 4px;
          font-size: 12px;
          font-weight: 600;
          color: var(--accent, #6d5df6);
          cursor: pointer;
        }
        .qf-reset:hover { text-decoration: underline; }

        .filter-dropdown .dropdown-section { padding: 6px; }
        .filter-dropdown .dropdown-header {
          padding: 6px 8px 4px;
          font-size: 10.5px;
          font-weight: 700;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: var(--t-500);
        }
        .filter-dropdown .dropdown-divider {
          height: 1px;
          margin: 0;
          background: var(--line);
        }

        .filter-dropdown .qf-item {
          display: flex;
          align-items: center;
          gap: 8px;
          width: 100%;
          padding: 8px;
          border-radius: var(--r-sm);
          font-size: 13px;
          font-weight: 500;
          color: var(--t-700);
          min-height: 38px;
        }
        /* A fixed slot for the tick: without it, selecting an item shifts every
           label sideways and the list appears to jump. */
        .filter-dropdown .qf-check {
          flex: none;
          width: 14px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          color: var(--accent, #6d5df6);
        }
        .filter-dropdown .qf-icon { flex: none; color: var(--t-500); }
        .filter-dropdown .qf-label { flex: 1; text-align: left; }
        .filter-dropdown .dropdown-count {
          flex: none;
          font-size: 11.5px;
          font-weight: 600;
          color: var(--t-500);
          background: var(--bg-inset);
          border-radius: 999px;
          padding: 1px 8px;
          font-variant-numeric: tabular-nums;
        }
        .filter-dropdown .qf-item.active {
          background: var(--accent-soft, #ece9ff);
          color: var(--accent-ink, #5847d6);
          font-weight: 600;
        }
        .filter-dropdown .qf-item.active .qf-icon { color: var(--accent-ink, #5847d6); }
        .filter-dropdown .qf-item.active .dropdown-count {
          background: rgba(255, 255, 255, 0.7);
          color: var(--accent-ink, #5847d6);
        }

        /* The toolbar button reports that filters are on, so it is visible
           without opening the menu. */
        .btn-toolbar.is-filtered {
          border-color: var(--accent, #6d5df6);
          color: var(--accent-ink, #5847d6);
        }
        .qf-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 18px;
          height: 18px;
          margin-left: 2px;
          padding: 0 5px;
          border-radius: 999px;
          background: var(--accent, #6d5df6);
          color: #fff;
          font-size: 11px;
          font-weight: 700;
          font-variant-numeric: tabular-nums;
        }

        /* Filter transitions.
           The old rows stay put and dim while the next page loads, then the new
           ones fade in. Swapping straight to a skeleton would collapse the table
           height and snap it back, which reads as a flicker. */
        .tx-fade { transition: opacity 180ms ease; }
        .tx-fade.is-refetching {
          opacity: 0.45;
          /* Clicks during the fade would land on rows about to be replaced. */
          pointer-events: none;
        }
        .tx-results { animation: tx-fade-in 220ms ease both; }
        @keyframes tx-fade-in {
          from { opacity: 0; transform: translateY(3px); }
          to   { opacity: 1; transform: none; }
        }
        /* Honour a stated preference for less motion: the fade still conveys
           the change, the movement is what gets dropped. */
        @media (prefers-reduced-motion: reduce) {
          .tx-fade { transition: none; }
          .tx-results { animation: none; }
        }

        .tx-type-pill {
          display: inline-flex;
          padding: 0.125rem 0.5rem;
          background: var(--bg-inset);
          border-radius: var(--r-sm);
          font-size: 0.75rem;
          font-weight: 600;
          color: var(--t-700);
          white-space: nowrap;
        }
        /* Money in, against something the merchant raised. */
        .tx-type-pill--in {
          background: var(--accent-soft, #ece9ff);
          color: var(--accent-ink, #5847d6);
        }
        /* Money out. */
        .tx-type-pill--out {
          background: var(--bg-inset);
          color: var(--t-700);
        }
        /* Arrived with nothing behind it. Tinted amber rather than red: it is
           real money and perfectly valid, it just reconciles against nothing,
           and that is worth noticing without implying a failure. */
        .tx-type-pill--untracked {
          background: var(--warn-soft, #fef3e0);
          color: var(--warn-ink, #b9760a);
        }

        /* Transaction Detail Drawer Styles */
        .tx-drawer-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          z-index: 999;
        }

        .tx-detail-drawer {
          position: fixed;
          top: 0;
          right: 0;
          width: 600px;
          max-width: 100%;
          height: 100vh;
          background: var(--bg-secondary);
          z-index: 1000;
          display: flex;
          flex-direction: column;
          box-shadow: -4px 0 20px rgba(0, 0, 0, 0.15);
          animation: txSlideIn 0.3s ease-out;
        }

        @keyframes txSlideIn {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }

        .tx-drawer-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.75rem 1rem;
          border-bottom: 1px solid var(--border-color);
          background: var(--bg-primary);
        }

        .tx-drawer-header-left {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .tx-close-btn {
          background: transparent;
          border: none;
          color: var(--text-secondary);
          cursor: pointer;
          padding: 0.5rem;
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .tx-close-btn:hover {
          background: var(--bg-secondary);
          color: var(--text-primary);
        }

        .tx-drawer-title-section {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .tx-drawer-icon-wrapper {
          width: 36px;
          height: 36px;
          background: var(--brand-100);
          border-radius: var(--r-sm);
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--brand-600);
        }

        .tx-drawer-title-section h2 {
          margin: 0;
          font-size: 1rem;
          font-weight: 600;
          color: var(--text-primary);
        }

        .tx-id-text {
          font-size: 0.75rem;
          color: var(--text-secondary);
          font-family: 'Monaco', 'Menlo', monospace;
        }

        .tx-drawer-header-right {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .tx-btn-outline {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 0.875rem;
          background: var(--bg-primary);
          color: var(--text-primary);
          border: 1px solid var(--border-color);
          border-radius: 6px;
          font-size: 0.85rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.15s;
        }

        .tx-btn-outline:hover {
          background: var(--bg-secondary);
          border-color: var(--border-color);
        }

        .tx-drawer-content {
          flex: 1;
          overflow-y: auto;
          padding: 1.5rem;
        }

        .tx-title-section {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.5rem;
        }

        /* Asset paid in, on the right of the amount row. */
        .tx-title-asset { flex-shrink: 0; }

        .tx-title-left {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .tx-title-left h1 {
          margin: 0;
          font-size: 1.5rem;
          font-weight: 600;
          color: var(--text-primary);
        }

        .tx-status-badge {
          display: inline-flex;
          padding: 0.25rem 0.625rem;
          border-radius: 4px;
          font-size: 0.75rem;
          font-weight: 500;
        }

        .tx-status-badge.status-succeeded {
          background: var(--ok-soft);
          color: var(--ok-ink);
        }

        .tx-status-badge.status-pending,
        .tx-status-badge.status-processing {
          background: var(--warn-soft);
          color: var(--warn-ink);
        }

        .tx-status-badge.status-failed {
          background: var(--err-soft);
          color: var(--err-ink);
        }

        .tx-status-badge.status-refunded {
          background: var(--brand-100);
          color: var(--brand-600);
        }

        .tx-status-badge.status-created {
          background: var(--bg-secondary);
          color: var(--text-secondary);
        }

        .tx-status-badge.status-not-supported {
          background: var(--err-soft);
          color: var(--err-ink);
        }

        .tx-subtitle-text {
          color: var(--text-secondary);
          font-size: 0.9rem;
          margin: 0 0 1.5rem 0;
        }

        .tx-id-bar {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 1.5rem;
          padding: 0.625rem 0.875rem;
          background: var(--bg-primary);
          border: 1px solid var(--border-color);
          border-radius: 8px;
        }

        .tx-id-icon {
          color: var(--text-muted);
          flex-shrink: 0;
        }

        .tx-id-input {
          flex: 1;
          background: transparent;
          border: none;
          outline: none;
          font-size: 0.8125rem;
          color: var(--text-primary);
          font-family: 'Monaco', 'Menlo', monospace;
        }

        .tx-copy-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0.375rem;
          background: transparent;
          border: none;
          color: var(--text-muted);
          cursor: pointer;
          border-radius: 4px;
        }

        .tx-copy-btn:hover {
          color: var(--accent-primary);
          background: var(--bg-secondary);
        }

        /* Cards */
        .tx-card {
          background: var(--bg-primary);
          border: 1px solid var(--border-color);
          border-radius: 8px;
          padding: 1.25rem;
          margin-bottom: 1rem;
        }

        .tx-card h3 {
          margin: 0 0 1rem 0;
          font-size: 0.9375rem;
          font-weight: 600;
          color: var(--text-primary);
        }

        .tx-card-header-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
        }

        .tx-card-header-row h3 {
          margin: 0;
        }

        /* Activity List */
        .tx-activity-list {
          display: flex;
          flex-direction: column;
          gap: 0.875rem;
        }

        .tx-activity-item {
          display: flex;
          align-items: flex-start;
          gap: 0.75rem;
        }

        .tx-activity-icon-wrapper {
          width: 28px;
          height: 28px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          background: var(--bg-secondary);
          color: var(--text-secondary);
        }

        .tx-activity-icon-wrapper.success {
          background: var(--ok-soft);
          color: var(--ok-ink);
        }

        .tx-activity-icon-wrapper.primary {
          background: var(--brand-100);
          color: var(--brand-600);
        }

        .tx-activity-icon-wrapper.warning {
          background: var(--warn-soft);
          color: var(--warn-ink);
        }

        .tx-activity-icon-wrapper.danger {
          background: var(--err-soft);
          color: var(--err-ink);
        }

        .tx-activity-content {
          display: flex;
          flex-direction: column;
        }

        .tx-activity-message {
          color: var(--text-primary);
          font-size: 0.875rem;
        }

        .tx-activity-date {
          color: var(--text-muted);
          font-size: 0.8rem;
        }

        /* Summary Grid */
        .tx-summary-grid {
          display: flex;
          gap: 3rem;
        }

        .tx-summary-left {
          flex: 1;
        }

        .tx-summary-right {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .tx-summary-field {
          margin-bottom: 0.75rem;
        }

        .tx-summary-field:last-child {
          margin-bottom: 0;
        }

        .tx-summary-field label {
          display: block;
          font-size: 0.75rem;
          color: var(--text-secondary);
          margin-bottom: 0.25rem;
        }

        .tx-summary-value {
          color: var(--text-primary);
          font-size: 0.875rem;
          display: flex;
          flex-direction: column;
        }

        /* Icon + label on one line, overriding the column default above. */
        .tx-summary-value--asset {
          flex-direction: row;
          align-items: center;
          gap: 0.4rem;
        }

        .tx-summary-value.tx-mono {
          font-family: 'Monaco', 'Menlo', monospace;
          font-size: 0.8rem;
        }

        .tx-summary-subtext {
          color: var(--text-secondary);
          font-size: 0.8rem;
        }

        /* Totals Block */
        .tx-totals-block {
          border-top: 1px solid var(--border-color);
          padding-top: 0.75rem;
        }

        .tx-total-row {
          display: flex;
          justify-content: space-between;
          padding: 0.375rem 0;
          font-size: 0.875rem;
          color: var(--text-secondary);
        }

        /* The received amount leads the card now, so it carries the emphasis
           rather than a summary row at the bottom. */
        .tx-total-row.tx-total-main {
          font-weight: 600;
          color: var(--text-primary);
          font-size: 0.95rem;
          padding-top: 0;
          margin-top: 0;
          border-top: none;
          padding-bottom: 0.5rem;
        }

        /* Any value that names an asset or a chain puts its mark beside it. */
        .tx-amount-asset {
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          text-align: right;
          flex-wrap: wrap;
          justify-content: flex-end;
        }
        .tx-amount-asset img,
        .tx-amount-asset svg { flex: none; }

        /* Settlement closes the card, so it takes the dividing rule the old
           "Total" row used to own. */
        .tx-total-row.tx-settles-row {
          align-items: center;
          padding-top: 0.625rem;
          margin-top: 0.25rem;
          border-top: 1px solid var(--border-color);
          color: var(--text-primary);
          font-weight: 600;
        }
        /* A route can name several protocols; let it wrap rather than run past
           the edge of the card. */
        .tx-route-value {
          text-align: right;
          word-break: break-word;
        }

        /* The chain a conversion leg sits on, secondary to the amount. */
        .tx-settles-chain {
          font-weight: 400;
          font-size: 0.8rem;
          color: var(--text-secondary);
        }
        /* Only the destination row takes the dividing rule, so "Paid in" and
           "Converted to" read as one pair rather than two separate facts. */
        .tx-total-row.tx-settles-row--to {
          border-top: none;
          padding-top: 0.25rem;
        }

        /* Says which of the two settlement paths applied, without competing
           with the asset it qualifies. */
        .tx-settles-note {
          font-style: normal;
          font-size: 0.7rem;
          font-weight: 600;
          letter-spacing: 0.03em;
          text-transform: uppercase;
          color: var(--text-muted, #6c7189);
          background: var(--bg-tertiary, #eff0f8);
          border-radius: 999px;
          padding: 2px 8px;
        }

        /* Blockchain Details */
        .tx-blockchain-details {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .tx-blockchain-row {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .tx-blockchain-row label {
          font-size: 0.75rem;
          color: var(--text-secondary);
        }

        .tx-hash-value {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .tx-hash-text {
          font-family: 'Monaco', 'Menlo', monospace;
          font-size: 0.8rem;
          color: var(--text-primary);
          word-break: break-all;
          flex: 1;
        }

        .tx-address-text {
          font-family: 'Monaco', 'Menlo', monospace;
          font-size: 0.8rem;
          color: var(--text-primary);
          word-break: break-all;
        }

        .tx-copy-btn-small {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0.25rem;
          background: transparent;
          border: none;
          color: var(--text-muted);
          cursor: pointer;
          border-radius: 4px;
          flex-shrink: 0;
        }

        .tx-copy-btn-small:hover {
          color: var(--accent-primary);
          background: var(--bg-secondary);
        }

        /* Events List */
        .tx-events-list {
          display: flex;
          flex-direction: column;
        }

        .tx-event-row {
          display: flex;
          justify-content: space-between;
          padding: 0.625rem 0;
          border-bottom: 1px solid var(--border-color);
        }

        .tx-event-row:last-child {
          border-bottom: none;
        }

        .tx-event-text {
          color: var(--text-primary);
          font-size: 0.875rem;
        }

        .tx-event-time {
          color: var(--text-muted);
          font-size: 0.8rem;
        }

        /* Block Confirmation Card Styles */
        .tx-confirmation-card {
          border: 1px solid var(--border-color);
          background: linear-gradient(135deg, var(--bg-primary) 0%, var(--bg-secondary) 100%);
        }

        .tx-confirmation-card h3 {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .tx-card-icon {
          color: var(--accent-primary);
        }

        .tx-confirmation-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.375rem;
          padding: 0.25rem 0.75rem;
          border-radius: 100px;
          font-size: 0.75rem;
          font-weight: 500;
        }

        .tx-confirmation-badge.confirmation-pending {
          background: var(--warn-soft);
          color: var(--warn-ink);
        }

        .tx-confirmation-badge.confirmation-confirmed {
          background: var(--ok-soft);
          color: var(--ok-ink);
        }

        .tx-confirmation-badge.confirmation-failed {
          background: var(--err-soft);
          color: var(--err-ink);
        }

        .spinning {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .tx-confirmation-content {
          margin-top: 1rem;
        }

        .tx-confirmation-progress {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .tx-confirmation-info {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .tx-confirmation-label {
          font-size: 0.9rem;
          font-weight: 500;
          color: var(--text-primary);
        }

        .tx-confirmation-sublabel {
          font-size: 0.8rem;
          color: var(--text-secondary);
        }

        .tx-confirmation-bar-container {
          width: 100%;
          height: 8px;
          background: var(--bg-secondary);
          border-radius: 100px;
          overflow: hidden;
        }

        .tx-confirmation-bar {
          height: 100%;
          background: linear-gradient(90deg, var(--warn), var(--warn-ink));
          border-radius: 100px;
          transition: width 0.5s ease;
        }

        .tx-confirmation-counts {
          display: flex;
          justify-content: space-between;
          font-size: 0.75rem;
          color: var(--text-secondary);
        }

        .tx-confirmation-remaining {
          color: var(--text-muted);
        }

        .tx-confirmation-complete {
          display: flex;
          align-items: flex-start;
          gap: 0.75rem;
        }

        .tx-confirmation-success-icon {
          color: var(--ok-ink);
          flex-shrink: 0;
        }

        .tx-confirmation-success-text {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .tx-confirmation-time {
          font-size: 0.75rem;
          color: var(--text-muted);
        }

        .tx-confirmation-failed {
          display: flex;
          align-items: flex-start;
          gap: 0.75rem;
        }

        .tx-confirmation-failed-icon {
          color: var(--err-ink);
          flex-shrink: 0;
        }

        .tx-confirmation-failed-text {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .tx-webhook-status {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-top: 1rem;
          padding-top: 1rem;
          border-top: 1px solid var(--border-color);
          font-size: 0.8rem;
          color: var(--ok-ink);
        }

        .tx-webhook-time {
          margin-left: auto;
          color: var(--text-muted);
        }

        /* Auto-conversion card (PRD §3.2) */
        .tx-conversion-card h3 {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .tx-conversion-pill {
          display: inline-flex;
          align-items: center;
          gap: 0.375rem;
          padding: 0.25rem 0.75rem;
          border-radius: 100px;
          font-size: 0.75rem;
          font-weight: 500;
          background: var(--warn-soft);
          color: var(--warn-ink);
        }

        .tx-conversion-flow {
          display: flex;
          flex-direction: column;
          gap: 0.875rem;
          margin-bottom: 1rem;
        }

        .tx-conversion-leg {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .tx-conversion-leg-text {
          display: flex;
          flex-direction: column;
          gap: 0.125rem;
        }

        .tx-conversion-leg-label {
          font-size: 0.75rem;
          color: var(--text-secondary);
        }

        .tx-conversion-leg-value {
          font-size: 0.875rem;
          font-weight: 500;
          color: var(--text-primary);
        }

        .tx-conversion-net {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding-top: 0.75rem;
          border-top: 1px solid var(--border-color);
          font-size: 0.875rem;
          color: var(--text-secondary);
        }

        .tx-conversion-net strong {
          color: var(--text-primary);
          font-size: 1rem;
        }

        .tx-conversion-pending-note {
          margin: 0;
        }

        .tx-conversion-details {
          border-top: 1px solid var(--border-color);
          padding-top: 0.75rem;
        }

        .tx-conversion-hash {
          display: inline-flex;
          align-items: center;
          gap: 0.375rem;
        }

        .tx-conversion-hash .tx-mono {
          font-family: 'Monaco', 'Menlo', monospace;
          font-size: 0.8rem;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .tx-detail-drawer {
            width: 100%;
          }

          .tx-summary-grid {
            flex-direction: column;
            gap: 1.5rem;
          }

          .tx-drawer-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.75rem;
          }

          .tx-drawer-header-right {
            width: 100%;
          }

          .tx-btn-outline {
            width: 100%;
            justify-content: center;
          }
        }

        .tx-pager-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          margin-top: 1rem;
          flex-wrap: wrap;
        }

        .tx-pager-row .pager { flex: 1; min-width: 0; }

        .table-toolbar--secondary {
          margin: 0.75rem 0;
        }

        .search-box {
  position: relative; /* 🔥 REQUIRED */
  display: flex;
  align-items: center;
}

.search-box input {
  width: 100%;
  padding: 8px 60px 8px 36px; /* space for icons */
}

.search-icon {
  position: absolute;
  left: 12px;
color: var(--text-secondary);
}

.search-clear {
  position: absolute;
  right: 12px; /* 🔥 inside input */
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
color: var(--text-secondary);
}

.search-clear:hover {
  color: var(--text-primary);
}

/* TX Hash Column Styles */
.txhash-cell {
  font-family: 'Monaco', 'Menlo', monospace;
  font-size: 0.8rem;
}

.txhash-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.txhash-text {
  color: var(--text-primary);
  cursor: default;
}

.txhash-copy-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem;
  background: transparent;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  border-radius: 4px;
  opacity: 0;
  transition: opacity 0.15s, color 0.15s;
}

.txhash-info:hover .txhash-copy-btn {
  opacity: 1;
}

.txhash-copy-btn:hover {
  color: var(--accent-primary);
  background: var(--bg-secondary);
}
@media (max-width: 576px) {
.tx-pager-row {
    display: inline-grid;
    justify-content: unset;
}
    }
      `}</style>
    </div>
  );
};

export default Transactions;
